// src/components/workflows/nodes/EventWaitNode.jsx - Event Wait Node for Workflows
import React, { useState, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';

import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Clock, Edit2, Flag } from 'lucide-react';

import { getAvailableEntities } from '../../../config/workflowEntities';

/**
 * EventWaitNode - Wait for external events to occur
 *
 * Pauses workflow execution until a specific event fires.
 * This is different from WaitNode (time-based delay) - this waits for actual events.
 *
 * Node data structure (BACKEND-ALIGNED):
 * {
 *   id: 'event-wait-uuid',
 *   type: 'eventWait',
 *   data: {
 *     waitForEvent: 'appointment_completed',
 *     matchType: 'any',  // ← NEW: 'any', 'for_appointment', 'unlinked'
 *     timeoutDays: 30,
 *     onTimeout: 'fail',
 *     description: '',
 *
 *     // Stage gate properties (optional)
 *     isStageGate: false,
 *     stageName: '',
 *     stageColor: 'blue'
 *   }
 * }
 */
const EventWaitNode = ({ data, selected, id }) => {
  const {
    waitForEvent = 'appointment_completed',
    matchType = 'any',
    timeoutDays = 30,
    onTimeout = 'fail',
    description = '',
    isStageGate = false,
    stageName = '',
    stageColor = 'blue'
  } = data;

  const { updateNodeData, getNodes } = useReactFlow();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description);

  // Get available entities based on workflow entry node
  const availableEntities = useMemo(() => {
    const nodes = getNodes();
    return getAvailableEntities(nodes, id);
  }, [getNodes, id]);

  // Check if appointment is tracked
  const hasAppointment = availableEntities.includes('appointment');

  // Event type options
  const eventTypes = [
    // Appointment events
    { value: 'appointment_completed', label: 'Appointment Completed' },
    { value: 'appointment_cancelled', label: 'Appointment Cancelled' },

    // Invoice events
    { value: 'invoice_sent', label: 'Invoice Sent' },
    { value: 'invoice_paid', label: 'Invoice Paid' },
    { value: 'invoice_overdue', label: 'Invoice Overdue' },

    // Customer data events (NEW)
    { value: 'customer_field_updated', label: 'Custom Field Updated' },
    { value: 'document_uploaded', label: 'Document Uploaded' },
    { value: 'document_removed', label: 'Document Removed' },

    // Manual approval events (NEW)
    { value: 'manual_approval_completed', label: 'Manual Approval Completed' },

    // Email/Form events
    { value: 'form_submitted', label: 'Form Submitted' },
    { value: 'email_opened', label: 'Email Opened' },
    { value: 'email_clicked', label: 'Email Link Clicked' },
  ];

  // Get friendly event label
  const getEventLabel = (eventValue) => {
    const event = eventTypes.find(e => e.value === eventValue);
    return event ? event.label : eventValue;
  };

  // Match type options based on available entities and selected event
  const matchTypeOptions = useMemo(() => {
    const eventLabel = getEventLabel(waitForEvent);
    const options = [
      { value: 'any', label: `Any ${eventLabel}`, description: 'Match any occurrence for this customer' }
    ];

    // If appointment is tracked and event is invoice-related, offer appointment matching
    if (hasAppointment && (waitForEvent === 'invoice_sent' || waitForEvent === 'invoice_paid' || waitForEvent === 'invoice_overdue')) {
      options.push({
        value: 'for_appointment',
        label: `${eventLabel} for this Appointment`,
        description: 'Only match if linked to the tracked appointment'
      });
      options.push({
        value: 'unlinked',
        label: `${eventLabel} (No Appointment)`,
        description: 'Only match if not linked to any appointment'
      });
    }

    return options;
  }, [waitForEvent, hasAppointment, eventTypes]);

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  return (
    <>
      {/* Input handle (left side) */}
      <Handle isConnectable={1}
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-cyan-500"
      />

      <div className={`
        w-72 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-cyan-500 shadow-lg border-cyan-500/50' : 'shadow-md border-cyan-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-cyan-500/10 to-cyan-600/5
        relative
      `}>
        {/* Stage gate indicator flag */}
        {isStageGate && (
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center shadow-lg z-10 border-2 border-slate-950">
            <Flag className="w-3.5 h-3.5 text-white" />
          </div>
        )}

        {/* Header */}
        <div className="p-3 pb-2 border-b border-cyan-500/10">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-cyan-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
              Event Wait
            </div>
            <Badge variant="outline" className="text-xs border-cyan-500/50 text-cyan-300">
              Wait
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-3">
          {/* Event Type Selection */}
          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-medium">Wait for event:</label>
            <Select
              value={waitForEvent}
              onValueChange={(value) => updateNodeData(id, { ...data, waitForEvent: value })}
            >
              <SelectTrigger className="w-full bg-cyan-500/10 border-cyan-500/30 text-cyan-200 text-sm nodrag">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {eventTypes.map(evt => (
                  <SelectItem key={evt.value} value={evt.value} className="text-slate-200">
                    {evt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Match Type Configuration */}
          {matchTypeOptions.length > 1 && (
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium">Match condition:</label>
              <Select
                value={matchType}
                onValueChange={(value) => updateNodeData(id, { ...data, matchType: value })}
              >
                <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-sm nodrag">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {matchTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-slate-200">
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        <span className="text-xs text-slate-400">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Timeout Configuration */}
          <div className="flex items-center gap-2">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs text-slate-400 font-medium">Timeout:</label>
              <Input
                type="number"
                min="1"
                value={timeoutDays}
                onChange={(e) => updateNodeData(id, { ...data, timeoutDays: parseInt(e.target.value) || 30 })}
                className="text-sm bg-slate-800/50 border-slate-700 text-slate-200 nodrag"
              />
            </div>
            <div className="pt-5 text-xs text-slate-400">days</div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-medium">On timeout:</label>
            <Select
              value={onTimeout}
              onValueChange={(value) => updateNodeData(id, { ...data, onTimeout: value })}
            >
              <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-sm nodrag">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="fail" className="text-slate-200">
                  Fail (mark as failed)
                </SelectItem>
                <SelectItem value="continue" className="text-slate-200">
                  Continue (proceed to next node)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="border-t border-cyan-500/10 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') {
                    setEditedDescription(description);
                    setIsEditingDescription(false);
                  }
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 nodrag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer nodrag"
                onClick={() => {
                  setEditedDescription(description);
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic text-center">
                    {description}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    Add description...
                  </div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-cyan-400" />
              </div>
            )}
          </div>

          {/* Stage Gate Toggle Section */}
          <div className="border-t border-cyan-500/10 pt-3">
            <label className="flex items-center gap-2 cursor-pointer nodrag">
              <input
                type="checkbox"
                checked={isStageGate}
                onChange={(e) => updateNodeData(id, {
                  ...data,
                  isStageGate: e.target.checked,
                  stageName: e.target.checked && !stageName ? 'New Stage' : stageName
                })}
                className="w-4 h-4 text-purple-500 bg-slate-800 border-slate-600 rounded focus:ring-purple-500"
              />
              <span className="text-xs text-slate-300 font-medium">Make this a stage gate</span>
            </label>

            {isStageGate && (
              <div className="mt-2 space-y-2 nodrag">
                <Input
                  placeholder="Stage name (e.g., 'Waiting for Appointment')"
                  value={stageName}
                  onChange={(e) => updateNodeData(id, {
                    ...data,
                    stageName: e.target.value
                  })}
                  className="text-xs bg-slate-800/50 border-slate-700 text-slate-200"
                />
                <Select
                  value={stageColor}
                  onValueChange={(color) => updateNodeData(id, {
                    ...data,
                    stageColor: color
                  })}
                >
                  <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="purple" className="text-slate-200">Purple</SelectItem>
                    <SelectItem value="blue" className="text-slate-200">Blue</SelectItem>
                    <SelectItem value="green" className="text-slate-200">Green</SelectItem>
                    <SelectItem value="orange" className="text-slate-200">Orange</SelectItem>
                    <SelectItem value="red" className="text-slate-200">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Output handle (right side) */}
      <Handle isConnectable={1}
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-cyan-500"
      />
    </>
  );
};

export default EventWaitNode;
